import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { EmptyState, ErrorState, LoadingBlock } from "../components/Feedback";
import { ApiError, api } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useToast } from "../lib/toast";
import { useAsync } from "../lib/useAsync";
import type { FeedbackKind, FeedbackStatus } from "../lib/types";

export const FEEDBACK_KINDS: Array<[FeedbackKind, string, string]> = [
  ["bug", "Something is broken", "A button that does nothing, an error, a lab check that seems wrong."],
  ["content", "A problem in a lesson", "A mistake, something unclear, a command that does not do what the lesson says."],
  ["idea", "An idea", "Something that would make the course better."],
  ["other", "Something else", "Anything you would like the team to know."],
];

export const FEEDBACK_STATUS: Record<FeedbackStatus, [string, string]> = {
  new: ["Received", "pill pill-pending"],
  looking: ["Looking into it", "pill pill-admin"],
  fixed: ["Fixed", "pill pill-pass"],
  closed: ["Closed", "pill pill-off"],
};

const MIN = 10;
const MAX = 4000;

/**
 * Feedback and bug reports to the team. A lesson's "Report a problem" link arrives here with
 * ?kind=content&page=<the lesson>, so the report says where it came from.
 */
export default function Feedback() {
  const toast = useToast();
  const [params] = useSearchParams();
  const mine = useAsync((signal) => api.myFeedback(signal), []);

  const initialKind = FEEDBACK_KINDS.some(([k]) => k === params.get("kind")) ? params.get("kind") as FeedbackKind : "bug";
  const [kind, setKind] = useState<FeedbackKind>(initialKind);
  const [page, setPage] = useState<string | null>(() => {
    const p = params.get("page");
    return p && p.startsWith("/") && !p.startsWith("//") ? p : null;
  });
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSending(true);
    setError(null);
    try {
      await api.sendFeedback({ kind, message: message.trim(), ...(page ? { page } : {}) });
      setMessage("");
      setPage(null);
      await mine.reload();
      toast.success("Thank you", "Your feedback has reached the team. Replies appear below and by email.");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Your feedback could not be sent. Try again.");
    } finally {
      setSending(false);
    }
  };

  const length = message.trim().length;

  return (
    <AppShell>
      <div className="account">
        <header className="section-head">
          <h2>Feedback</h2>
          <span className="spread dim">Bugs, mistakes in lessons, ideas: it all goes straight to the team.</span>
        </header>

        <section className="panel" aria-labelledby="send-heading">
          <div className="panel-head">
            <h3 id="send-heading">Tell us</h3>
          </div>
          <form className="panel-pad stack" onSubmit={submit} noValidate>
            {error ? <p className="form-error" role="alert">{error}</p> : null}

            <fieldset className="field feedback-kinds">
              <legend>What is it about?</legend>
              {FEEDBACK_KINDS.map(([value, label, hint]) => (
                <label key={value} className={kind === value ? "feedback-kind is-chosen" : "feedback-kind"}>
                  <input type="radio" name="kind" value={value} checked={kind === value} onChange={() => setKind(value)} />
                  <span>
                    <strong>{label}</strong>
                    <span className="hint">{hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            {page ? (
              <p className="hint">
                About <span className="mono">{page}</span>{" "}
                <button type="button" className="btn btn-sm btn-quiet" onClick={() => setPage(null)}>Remove</button>
              </p>
            ) : null}

            <div className="field">
              <label htmlFor="feedback-message">
                {kind === "bug" ? "What happened, and what did you expect?" : "Your feedback"}
              </label>
              <textarea
                id="feedback-message"
                className="input"
                rows={7}
                maxLength={MAX}
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                aria-describedby="feedback-help"
              />
              <p className="hint" id="feedback-help">
                {kind === "bug"
                  ? "The steps you took and any error message help most. The page you were on and your browser are sent with it."
                  : `${MAX - message.length} characters left.`}
              </p>
            </div>

            <div>
              <button type="submit" className="btn btn-primary" disabled={sending || length < MIN}>
                {sending ? <span className="spinner" /> : null}
                Send feedback
              </button>
            </div>
          </form>
        </section>

        <section className="panel" aria-labelledby="sent-heading">
          <div className="panel-head">
            <h3 id="sent-heading">What you have sent</h3>
          </div>
          <div className="panel-pad">
            {mine.loading && !mine.data ? (
              <LoadingBlock label="Loading your feedback" />
            ) : mine.error ? (
              <ErrorState error={mine.error} onRetry={() => void mine.reload()} />
            ) : mine.data && mine.data.feedback.length > 0 ? (
              <div className="feedback-list">
                {mine.data.feedback.map((item) => {
                  const [label, pill] = FEEDBACK_STATUS[item.status];
                  return (
                    <article key={item.id} className="feedback-item">
                      <p className="row">
                        <span className={pill}>{label}</span>
                        <span className="dim">
                          {FEEDBACK_KINDS.find(([k]) => k === item.kind)?.[1]} · {formatDateTime(item.createdAt)}
                          {item.page ? <> · <span className="mono">{item.page}</span></> : null}
                        </span>
                      </p>
                      <p className="feedback-message">{item.message}</p>
                      {item.reply ? (
                        <div className="feedback-reply">
                          <strong>The team replied</strong>
                          <p>{item.reply}</p>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            ) : (
              <EmptyState icon="bulb" title="Nothing sent yet">
                Anything you send appears here, with its progress and our reply.
              </EmptyState>
            )}
          </div>
        </section>
      </div>
    </AppShell>
  );
}
